import { Link, useRouterState } from "@tanstack/react-router";
import {
  LuBell,
  LuBookOpen,
  LuBot,
  LuBriefcaseBusiness,
  LuCalendarRange,
  LuChevronDown,
  LuChevronRight,
  LuEye,
  LuEyeOff,
  LuExternalLink,
  LuGitFork,
  LuSettings,
  LuLayoutDashboard,
  LuMenu,
  LuUsersRound,
  LuX,
} from "react-icons/lu";
import type { IconType } from "react-icons";
import { useQuery } from "@/lib/convex";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Collapsible,
  Drawer,
  Flex,
  HStack,
  Icon,
  IconButton,
  Image,
  NativeSelect,
  Portal,
  Text,
} from "@chakra-ui/react";
import type { FunctionReturnType } from "convex/server";
import type { Id } from "@convex/_generated/dataModel";
import { api } from "@convex/_generated/api";
import { useEffectivePersonId, useImpersonation } from "../impersonation";
import { useViewerAccess } from "@/lib/access";
import logo from "../assets/cemmbase-logo.svg";
import { Tooltip } from "./ui/tooltip";
import { LuShieldUser } from "./icons";
import { FeedbackButton } from "./FeedbackButton";

/**
 * Why an entry is shown to some people and not others.
 *
 * Every condition but `programme` is a role -- or, for `supervisor`, a
 * position in the org chart -- and those entries carry the role marker, so
 * that somebody who has one can tell which parts of the navigation are theirs
 * because of it. A programme is an enrollment, not a role: a student's own
 * programme page is simply theirs.
 */
type NavigationCondition =
  | "activityAdmin"
  // Reconciliation is HR's and IT's view of where the feeds disagree, and
  // leave is employment data: both are gated on those roles rather than on
  // activity administration.
  | "directoryAdmin"
  // The audit log shows one person what another did, so it is the sys admin's
  // alone -- not HR's, unlike the two above.
  | "sysAdmin"
  // Hidden outright for anybody without a programme rather than shown empty:
  // most of the institute has no enrollment, and a link to a page that will
  // tell them so is worse than no link.
  | "programme"
  | "supervisor"
  | "coSupervisor";

const ROLE_MARKER_TITLES: Record<Exclude<NavigationCondition, "programme">, string> = {
  activityAdmin: "Shown because you are an activity admin",
  directoryAdmin: "Shown because you are an HR or system admin",
  sysAdmin: "Shown because you are a system admin",
  supervisor: "Shown because you supervise people",
  coSupervisor: "Shown because you co-supervise students",
};

type NavigationChild = {
  to: string;
  label: string;
  requires?: NavigationCondition;
  // Outside the app, opened in a new tab. The Convex dashboard is the one
  // such entry: a separate application on the intranet.
  external?: boolean;
};

type NavigationItem = {
  to: string;
  label: string;
  icon: IconType;
  iconTransform?: string;
  requires?: NavigationCondition;
  // A section that exists only for its children: shown when at least one of
  // them is, and marked as role-gated itself rather than on every child.
  sectionOfRoleGatedPages?: boolean;
  children?: NavigationChild[];
};

const convexDashboardUrl = process.env.PUBLIC_CONVEX_DASHBOARD_URL as string | undefined;

const baseNavigation: NavigationItem[] = [
  {
    to: "/",
    label: "Dashboard",
    icon: LuLayoutDashboard,
    children: [
      // Specialised dashboards: both answer "how is this going" rather
      // than "what do I do next", which is what keeps them out of
      // Activities.
      { to: "/my-programme", label: "My programme", requires: "programme" },
      // "My group" rather than "My students": a group head supervises
      // postdocs, technicians and PhD students alike, and the page has always
      // listed all of them.
      //
      // It used to be shown to everyone, on the grounds that supervising is a
      // position in the org chart rather than a role on the account, so the
      // client could not tell whether the page had anything. It could not tell
      // because nothing asked; `viewerSupervisesAnyone` asks.
      { to: "/my-group", label: "My group", requires: "supervisor" },
      { to: "/co-supervision", label: "Co-supervision", requires: "coSupervisor" },
    ],
  },
  { to: "/notifications", label: "Notifications", icon: LuBell },
  // One section, because it is one module (model §4). The person's own page
  // leads, because that is where both halves of it -- what they owe and what
  // they took part in -- are equally reachable; the calendar is a sibling
  // rather than a separate module, which is what having it top-level said.
  {
    to: "/activities",
    label: "Events & Activities",
    icon: LuCalendarRange,
    children: [
      { to: "/events", label: "Calendar" },
      { to: "/events/planning", label: "Seminar planning", requires: "activityAdmin" },
      // Everyone sees this: a supervisor is not a role on the account, it is a
      // position in the org chart, so the client cannot tell in advance whether
      // the queue will have anything in it.
      { to: "/activities/reviews", label: "Reviews" },
      { to: "/activities/admin/progress", label: "Programme progress", requires: "activityAdmin" },
      { to: "/activities/admin/catalogue", label: "Activity catalogue", requires: "activityAdmin" },
      { to: "/activities/admin/assigned", label: "Assigned activities", requires: "activityAdmin" },
    ],
  },
  { to: "/research-output", label: "Research output", icon: LuBookOpen },
  { to: "/grants", label: "Grants", icon: LuBriefcaseBusiness },
  { to: "/cemm-chat", label: "CeMM Chat", icon: LuBot },
  {
    to: "/cemmies",
    label: "CeMMies",
    icon: LuUsersRound,
    children: [
      // HR's and IT's business, like the reconciliation report: leave is
      // employment data, and the activity module only reacts to it.
      { to: "/cemmies/leave", label: "Leave", requires: "directoryAdmin" },
    ],
  },
  { to: "/groups", label: "Groups", icon: LuGitFork, iconTransform: "rotate(180deg)" },
  {
    to: "/admin",
    label: "Administration",
    icon: LuSettings,
    sectionOfRoleGatedPages: true,
    children: [
      { to: "/admin/reconciliation", label: "Reconciliation", requires: "directoryAdmin" },
      { to: "/admin/audit-log", label: "Audit log", requires: "sysAdmin" },
      { to: "/admin/roles", label: "Roles", requires: "sysAdmin" },
      { to: "/admin/feedback", label: "Feedback", requires: "sysAdmin" },
      // Only where the build was told where it is: the address differs per
      // environment, and a local build without it should not offer a dead link.
      ...(convexDashboardUrl
        ? [{ to: convexDashboardUrl, label: "Convex dashboard", requires: "sysAdmin", external: true } as const]
        : []),
    ],
  },
];

/**
 * The one marker for "you see this because of a role": the same icon whichever
 * role it is, with the role named on hover.
 */
function RoleMarker({ condition }: { condition: NavigationCondition }) {
  if (condition === "programme") {
    return null;
  }
  const title = ROLE_MARKER_TITLES[condition];
  return (
    <Tooltip content={title}>
      <Box as="span" display="inline-flex" aria-label={title}>
        <Icon as={LuShieldUser} boxSize={3.5} color="fg.subtle" />
      </Box>
    </Tooltip>
  );
}

// Derived, not described. The hand-written version declared a `cohort` the
// query never sent and an `isActive` it never had, and the `as` cast below
// made both look fine until the option list read "cohort undefined" for all
// four hundred people.
type ImpersonationTarget =
  FunctionReturnType<typeof api.persons.listImpersonationTargets>[number];

function NavigationMenu({
  pathname,
  onNavigate,
  conditions,
  unreadNotifications,
}: {
  pathname: string;
  onNavigate?: () => void;
  conditions: Record<NavigationCondition, boolean>;
  unreadNotifications: number;
}) {
  const navigation = useMemo(
    () => baseNavigation
      .filter((item) => item.requires === undefined || conditions[item.requires])
      .map((item) => ({
        ...item,
        children: item.children?.filter((child) => child.requires === undefined || conditions[child.requires]),
      }))
      .filter((item) => !item.sectionOfRoleGatedPages || (item.children?.length ?? 0) > 0),
    [conditions],
  );

  return (
    <Box as="nav" w="full" fontSize="0.95rem">
      {navigation.map((item) => {
        const NavIcon = item.icon;
        const isActive =
          item.to === "/"
            ? pathname === "/"
            : pathname === item.to || pathname.startsWith(`${item.to}/`);
        const hasChildren = Boolean(item.children && item.children.length > 0);
        // A child may sit outside its parent's path -- the dashboard's do,
        // since every path begins with "/" and the parent therefore has to
        // match exactly. Asking the children directly keeps the group open
        // around whichever one is selected, instead of collapsing the
        // moment you pick it.
        // ONE child is highlighted: the longest of those the path is under.
        // Testing each child on its own lit two at once as soon as a child
        // sat beneath a sibling -- /events/planning is under /events, so both
        // Calendar and Seminar planning claimed it.
        const bestChildMatch = (item.children ?? [])
          .filter((child) => pathname === child.to || pathname.startsWith(`${child.to}/`))
          .sort((left, right) => right.to.length - left.to.length)[0]?.to ?? null;
        const expanded = isActive || bestChildMatch !== null;

        return (
          <Box key={item.to}>
            <Link to={item.to} onClick={onNavigate}>
              <HStack
                gap={3}
                px={5}
                py={3}
                fontWeight="medium"
                whiteSpace="nowrap"
                borderYWidth="1px"
                borderColor={expanded ? "border" : "transparent"}
                bg={expanded ? "bg.muted" : "transparent"}
                color={expanded ? "fg" : "fg.muted"}
                _hover={expanded ? undefined : { bg: "bg.muted", color: "fg" }}
                transition="colors 0.2s"
              >
                <Icon as={NavIcon} boxSize={5} transform={item.iconTransform} color={expanded ? "blue.500" : "fg.muted"} />
                <Box flex="1">{item.label}</Box>
                {/* A section of role-gated pages is marked once, here, rather
                    than on each of its children. */}
                {item.sectionOfRoleGatedPages && item.children?.[0]?.requires ? (
                  <RoleMarker condition={item.children[0].requires} />
                ) : item.requires ? (
                  <RoleMarker condition={item.requires} />
                ) : null}
                {/* Only where there is something to say. A zero badge is a
                    control that trains people to ignore it. */}
                {item.to === "/notifications" && unreadNotifications > 0 ? (
                  <Box
                    minW="1.35rem"
                    px={1.5}
                    rounded="full"
                    bg="blue.500"
                    color="white"
                    fontSize="xs"
                    fontWeight="bold"
                    textAlign="center"
                  >
                    {unreadNotifications}
                  </Box>
                ) : null}
                {hasChildren ? (
                  expanded ? (
                    <Icon as={LuChevronDown} boxSize={4} color="blue.500" />
                  ) : (
                    <Icon as={LuChevronRight} boxSize={4} color="fg.muted" />
                  )
                ) : (
                  <Icon as={LuChevronRight} boxSize={4} color={expanded ? "blue.500" : "fg.muted"} />
                )}
              </HStack>
            </Link>

            {hasChildren ? (
              <Collapsible.Root open={expanded}>
                <Collapsible.Content>
                  {/* Indented rail groups the children under their parent */}
                  <Box ml={7} my={1} borderLeftWidth="2px" borderColor="border">
                    {item.children?.map((child) => {
                      const childActive = child.to === bestChildMatch;
                      const row = (
                          <HStack
                            gap={2}
                            ml="-2px"
                            borderLeftWidth="2px"
                            borderColor={childActive ? "colorPalette.solid" : "transparent"}
                            colorPalette="blue"
                            px={4}
                            py={2}
                            fontSize="sm"
                            bg={childActive ? "colorPalette.subtle" : "transparent"}
                            fontWeight={childActive ? "medium" : "normal"}
                            color={childActive ? "colorPalette.fg" : "fg.muted"}
                            _hover={childActive ? undefined : { bg: "bg.muted", color: "fg" }}
                            transition="colors 0.15s"
                          >
                            <Box flex="1">{child.label}</Box>
                            {child.external ? <Icon as={LuExternalLink} boxSize={3.5} /> : null}
                            {child.requires && !item.sectionOfRoleGatedPages ? (
                              <RoleMarker condition={child.requires} />
                            ) : null}
                          </HStack>
                      );
                      return child.external ? (
                        <a key={child.to} href={child.to} target="_blank" rel="noreferrer" onClick={onNavigate}>
                          {row}
                        </a>
                      ) : (
                        <Link key={child.to} to={child.to} onClick={onNavigate}>
                          {row}
                        </Link>
                      );
                    })}
                  </Box>
                </Collapsible.Content>
              </Collapsible.Root>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}

function ImpersonationPanel() {
  const { impersonatedPersonId, setImpersonatedPersonId, clearImpersonation } = useImpersonation();
  const [droppedImpersonation, setDroppedImpersonation] = useState(false);
  // Asked with the remembered choice, not with null: that is what lets the
  // backend tell us the choice is no longer resolvable. `account` on the
  // profile is always the real viewer, so the capability check below is
  // unaffected.
  const profile = useQuery(api.persons.viewerProfile, {
    effectivePersonId: impersonatedPersonId,
  });

  // A remembered person who has since lost their account -- they left, or the
  // database was rebuilt underneath the choice. The backend already fell back
  // to the real viewer, so nothing is broken; forget the choice so it stops
  // being sent, and say what happened rather than silently changing whose data
  // is on screen.
  const impersonationUnavailable = profile !== undefined
    && profile !== null
    && "impersonation" in profile
    && profile.impersonation !== null
    && profile.impersonation.unavailable;

  useEffect(() => {
    if (impersonationUnavailable && impersonatedPersonId !== null) {
      clearImpersonation();
      setDroppedImpersonation(true);
    }
  }, [impersonationUnavailable, impersonatedPersonId, clearImpersonation]);

  const canImpersonate = Boolean(
    profile
    && profile !== null
    && profile.status !== "unlinked"
    && profile.status !== "missing_person"
    && profile.account.assignedRoles.includes("sysAdmin"),
  );

  const targets = useQuery(
    api.persons.listImpersonationTargets,
    canImpersonate ? {} : "skip",
  ) as ImpersonationTarget[] | undefined;

  if (!canImpersonate) {
    return null;
  }

  const sortedTargets = [...(targets ?? [])].sort((first, second) => {
    const nameComparison = first.fullName.localeCompare(second.fullName, undefined, { sensitivity: "base" });
    if (nameComparison !== 0) {
      return nameComparison;
    }

    // Nullable: somebody can hold an account with no mail address on it, and
    // the hand-written type that claimed otherwise would have thrown here.
    return (first.email ?? "").localeCompare(second.email ?? "", undefined, { sensitivity: "base" });
  });
  const isActiveImpersonation = impersonatedPersonId !== null;

  return (
    <Box pt={3}>
      <Box mx={4} mb={3} borderTopWidth="1px" borderColor={isActiveImpersonation ? "orange.300" : "border"} />
      <HStack mx={4} mb={2} gap={2} fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em" color={isActiveImpersonation ? "orange.500" : "fg.muted"}>
        <Icon as={LuEye} boxSize={4} color={isActiveImpersonation ? "orange.500" : "blue.500"} />
        Impersonation
      </HStack>
      <Box mx={4}>
        <NativeSelect.Root size="sm">
          <NativeSelect.Field
            value={impersonatedPersonId ?? ""}
            onChange={(event) => {
              const nextValue = event.target.value;
              setImpersonatedPersonId(nextValue ? nextValue as Id<"persons"> : null);
            }}
          >
            <option value="">Use my own view</option>
            {sortedTargets.map((target) => (
              <option key={target.personId} value={target.personId}>
                {target.fullName}
                {target.email === null ? "" : ` · ${target.email}`}
                {target.cohort === null ? "" : ` · cohort ${target.cohort}`}
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
        {/* Solid rather than outlined, and the loudest thing in the sidebar
            while it is showing. Nobody arrives here by accident -- an admin
            chose this -- so the job is not to warn but to make the way back
            findable without hunting for it. */}
        {impersonatedPersonId ? (
          <Button mt={2} size="sm" colorPalette="orange" w="full" onClick={clearImpersonation}>
            <LuEyeOff />
            Stop impersonation
          </Button>
        ) : null}
        {droppedImpersonation ? (
          <Text mt={2} fontSize="xs" color="fg.muted">
            Impersonation ended: that person no longer has an account.
          </Text>
        ) : null}
      </Box>
    </Box>
  );
}

function SidebarProfileEntry({ footer }: { footer?: ReactNode }) {
  if (!footer) {
    return null;
  }

  return (
    <Box pt={3}>
      <Box mx={4} mb={3} borderTopWidth="1px" borderColor="border" />
      {footer}
    </Box>
  );
}

type SidebarProps = {
  footer?: ReactNode;
};

export function Sidebar({ footer }: SidebarProps) {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const effectivePersonId = useEffectivePersonId();

  // Everything the NAVIGATION offers follows impersonation, exactly as the
  // programme entry always did. The impersonation control itself is the one
  // thing that must not: it reads the real viewer, further up, or looking
  // as a student would hide the way back.
  const access = useViewerAccess();
  const canAccessActivityAdmin = access?.isActivityAdmin ?? false;
  const canAccessDirectoryAdmin = access?.isDirectoryAdmin ?? false;
  const canAccessSysAdmin = access?.isSysAdmin ?? false;

  const programme = useQuery(api.programmeProgress.viewerHasProgramme, {
    effectivePersonId: effectivePersonId as Id<"persons"> | null,
  });
  const hasProgramme = Boolean(programme?.hasProgramme);

  const supervision = useQuery(api.reviews.viewerSupervisesAnyone, {
    effectivePersonId: effectivePersonId as Id<"persons"> | null,
  });
  const supervisesAnyone = Boolean(supervision?.supervises);

  const coSupervision = useQuery(api.coSupervision.viewerCoSupervisesAnyone, {
    effectivePersonId: effectivePersonId as Id<"persons"> | null,
  });
  const coSupervisesAnyone = Boolean(coSupervision?.coSupervises);

  const unread = useQuery(api.notifications.unreadCount, {
    effectivePersonId: effectivePersonId as Id<"persons"> | null,
  });
  const unreadNotifications = unread ?? 0;

  // Memoised, because the menu's own memo is keyed on it.
  const conditions = useMemo(
    (): Record<NavigationCondition, boolean> => ({
      activityAdmin: canAccessActivityAdmin,
      directoryAdmin: canAccessDirectoryAdmin,
      sysAdmin: canAccessSysAdmin,
      programme: hasProgramme,
      supervisor: supervisesAnyone,
      coSupervisor: coSupervisesAnyone,
    }),
    [canAccessActivityAdmin, canAccessDirectoryAdmin, canAccessSysAdmin, hasProgramme, supervisesAnyone, coSupervisesAnyone],
  );

  return (
    <>
      <IconButton
        aria-label="Open navigation"
        variant="outline"
        bg="bg"
        position="fixed"
        right={4}
        top={4}
        zIndex={40}
        display={{ base: "inline-flex", lg: "none" }}
        onClick={() => setMobileOpen(true)}
      >
        <LuMenu />
      </IconButton>

      <Drawer.Root open={mobileOpen} onOpenChange={(e) => setMobileOpen(e.open)} placement="start" size="xs">
        <Portal>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content>
              <Flex align="center" justify="space-between" px={5} py={5}>
                <Link to="/" onClick={() => setMobileOpen(false)}>
                  <Image src={logo} alt="CeMMbase" h={9} w="auto" />
                </Link>
                <IconButton
                  aria-label="Close navigation"
                  variant="outline"
                  onClick={() => setMobileOpen(false)}
                >
                  <LuX />
                </IconButton>
              </Flex>
              <Flex flex="1" direction="column" overflowY="auto" py={4}>
                <NavigationMenu
                  pathname={pathname}
                  onNavigate={() => setMobileOpen(false)}
                  conditions={conditions}
                  unreadNotifications={unreadNotifications}
                />
                <Box mt="auto">
                  <FeedbackButton />
                  <ImpersonationPanel />
                  <SidebarProfileEntry footer={footer} />
                </Box>
              </Flex>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>

      <Box
        as="aside"
        display={{ base: "none", lg: "flex" }}
        flexDirection="column"
        flexShrink={0}
        h="100vh"
        w="64"
        borderRightWidth="1px"
        borderColor="border"
        bg="bg"
        position={{ base: "static", lg: "sticky" }}
        top={0}
      >
        <Flex align="center" justify="center" px={6} py={6}>
          <Link to="/">
            <Image src={logo} alt="CeMMbase" h={10} w="auto" />
          </Link>
        </Flex>

        <Flex minH={0} flex="1" direction="column" pb={4}>
          <Box overflowY="auto">
            <NavigationMenu
              pathname={pathname}
              conditions={conditions}
              unreadNotifications={unreadNotifications}
            />
          </Box>
          <Box mt="auto">
            <FeedbackButton />
            <ImpersonationPanel />
            <SidebarProfileEntry footer={footer} />
          </Box>
        </Flex>
      </Box>
    </>
  );
}
